
import { useState, useEffect, useMemo, useRef } from 'react';
import { collection, query, orderBy, onSnapshot, where, doc } from 'firebase/firestore';
import { useAppContext } from '../AppContext';

const useDreamWeaveData = (dreamWeaveId, nexusId) => {
    const { db } = useAppContext();
    const [contributions, setContributions] = useState([]);
    const [connections, setConnections] = useState([]);
    const [polls, setPolls] = useState([]);
    const [starlights, setStarlights] = useState([]);
    const [quests, setQuests] = useState([]);
    const [event, setEvent] = useState(null);
    const [constellations, setConstellations] = useState([]);
    const [souls, setSouls] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    
    // State to trigger re-renders for animation
    const [tick, setTick] = useState(0);
    const animationFrameId = useRef();

    const basePath = useMemo(() => {
        if (!dreamWeaveId) return null;
        return nexusId ? `nexuses/${nexusId}/dreamWeaves/${dreamWeaveId}` : `dreamWeaves/${dreamWeaveId}`;
    }, [dreamWeaveId, nexusId]);

    useEffect(() => {
        if (!db || !basePath) {
            setLoading(false);
            return () => {};
        }

        setLoading(true);
        const paths = {
            contributions: `${basePath}/contributions`,
            connections: `${basePath}/connections`,
            polls: `${basePath}/polls`,
            starlights: `${basePath}/starlights`,
            quests: `${basePath}/quests`,
            constellations: `${basePath}/constellations`,
        };

        const queries = {
            contributions: query(collection(db, paths.contributions), where("absorbedBy", "==", null)),
            connections: query(collection(db, paths.connections), orderBy("timestamp", "asc")),
            polls: query(collection(db, paths.polls), orderBy("createdAt", "asc")),
            starlights: query(collection(db, paths.starlights), orderBy("createdAt", "asc")),
            quests: query(collection(db, paths.quests), where("isActive", "==", true)),
            constellations: query(collection(db, paths.constellations), orderBy("createdAt", "asc")),
        };

        const unsubscribes = [
            onSnapshot(doc(db, basePath), (doc) => {
                setEvent(doc.data()?.event || null);
                setSouls(doc.data()?.souls || 0);
            }),
            onSnapshot(queries.contributions, snapshot => {
                setContributions(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
                setLoading(false);
            }, err => {
                console.error("Error fetching contributions:", err);
                setError(err);
                setLoading(false);
            }),
            onSnapshot(queries.connections, snapshot => setConnections(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))), err => console.error("Error fetching connections:", err)),
            onSnapshot(queries.polls, snapshot => setPolls(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))), err => console.error("Error fetching polls:", err)),
            onSnapshot(queries.starlights, snapshot => setStarlights(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))), err => console.error("Error fetching starlights:", err)),
            onSnapshot(queries.quests, snapshot => setQuests(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))), err => console.error("Error fetching quests:", err)),
            onSnapshot(queries.constellations, snapshot => setConstellations(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }))), err => console.error("Error fetching constellations:", err)),
        ];

        // Start the animation loop
        const animate = () => {
            setTick(prev => prev + 1);
            animationFrameId.current = requestAnimationFrame(animate);
        };
        animationFrameId.current = requestAnimationFrame(animate);

        return () => {
            unsubscribes.forEach(unsub => unsub());
            cancelAnimationFrame(animationFrameId.current);
        };
    }, [db, basePath]);

    const processedData = useMemo(() => {
        if (contributions.length === 0) {
            return { neurons: [], synapses: [] };
        }

        const time = Date.now() * 0.0001; // Time factor for animation speed

        const neurons = contributions.map((c, index) => {
            const initialPosition = c.position || { x: 0, y: 0, z: 0 };
            
            // Gentle sine wave-based movement for a floating effect
            const x = initialPosition.x + Math.sin(time + index * 0.5) * 0.5;
            const y = initialPosition.y + Math.cos(time + index * 0.3) * 0.5;
            const z = initialPosition.z + Math.sin(time + index * 0.7) * 0.5;

            return {
                id: c.id,
                ...c,
                position: [x, y, z], // Return as an array for the 3D library
                color: c.color || '#FFFFFF',
                size: c.size || 0.5,
            };
        });

        const synapses = connections.map(edge => {
            const fromNeuron = neurons.find(n => n.id === edge.from);
            const toNeuron = neurons.find(n => n.id === edge.to);
            if (fromNeuron && toNeuron) {
                return {
                    id: edge.id,
                    ...edge,
                    start: fromNeuron.position,
                    end: toNeuron.position,
                };
            }
            return null;
        }).filter(Boolean);

        return { neurons, synapses };
    }, [contributions, connections, tick]); // Re-calculate on each animation frame tick

    return { ...processedData, polls, starlights, quests, event, constellations, souls, loading, error };
};

export default useDreamWeaveData;
